import {
  isBeiyingDevice8815X98Keyboard,
  isKeyboardFirmwareUpgradeInProgress,
  isHuafendaDongleWebHid,
  isNk20ReceiverHid,
  HFD_24G_DONGLE_PRODUCT_IDS,
  huafendaConfigIdentifierMatchesDeviceId,
  jiangmengMouseStableIdsMatch,
  KEYBOARD_VID_PID_NEED_PRODUCT_SPLIT,
  resolveXingshanConfigurationRowForDevice,
  SDK_KEYBOARD_MOUSE_TYPE,
  SUPPORTED_DEVICES,
  stampOtherMenuOnlyIfGenericXingshanHid,
  xingshanHidMatchesConfiguration,
  rongyuanHidMatchesConfiguration,
  rongyuanHidCollectionMatches,
  isRongyuanMechanicalKeyboard,
  hasDeviceExternalUrl,
  findSupportedDeviceByVidPid,
  findSupportedDeviceByVidPidAndProductName,
  externalUrlHidProductNameMatches,
  isExternalUrlSupportedVidPid,
} from "@eweadn-qiankun/shared-utils";
import {
  ByHidFilter,
  ConnectType,
  DeviceDetector,
} from "@bytech/web-hid-sdk/keyboard";
import { hidPhysicalKey } from "./webHidAuth";
import { sdcxCardDedupeKey, zhwxCardDedupeKey } from "./cardDedupeKeys";

export {
  KEYBOARD_VID_PID_NEED_PRODUCT_SPLIT,
  resolveXingshanConfigurationRowForDevice,
  xingshanHidMatchesConfiguration,
  rongyuanHidMatchesConfiguration,
  rongyuanHidCollectionMatches,
  isRongyuanMechanicalKeyboard,
  hasDeviceExternalUrl,
  findSupportedDeviceByVidPid,
  findSupportedDeviceByVidPidAndProductName,
  externalUrlHidProductNameMatches,
  isExternalUrlSupportedVidPid,
  zhwxCardDedupeKey,
  sdcxCardDedupeKey,
};

/** 贝盈 web-hid 默认 VID */
const BEIYING_DEFAULT_VENDOR_ID = 0x372e;

/** 贝盈原生 8K 2.4G 接收器 PID（ES98 多配置行共用，需 UUID 探测） */
export const BEIYING_LEGACY_24G_PRODUCT_ID = 0x106c;

/** DeviceDetector：2.4G 接收器须标 isWireless，SDK 再按 PID 区分 8K/1K 读 UUID 协议 */
function isBeiyingDeviceDetectorWirelessFilter(row: {
  is4G?: boolean;
}): boolean {
  return row.is4G === true;
}

/** ES98 非 0x106c 接收器（如 0x10d5）仅探测当前 PID，避免与全家族混滤 */
function shouldUseEs98SingleProductHidFilter(configuration: {
  keyboardType?: string;
  productId?: number;
}): boolean {
  if (configuration?.keyboardType !== "99") return false;
  if (typeof configuration.productId !== "number") return false;
  return configuration.productId !== BEIYING_LEGACY_24G_PRODUCT_ID;
}

/** ES98（keyboardType 99）：DeviceDetector 需传入该型号全部 PID 的 ByHidFilter（有线 + 2.4G 等） */
export function buildBeiyingEs98HidFilters(): ByHidFilter[] {
  const keyboard = SUPPORTED_DEVICES.keyboard as unknown as Record<
    string,
    unknown
  >[];
  const byPid = new Map<number, { is4G?: boolean; vendorId?: number }>();
  for (const row of keyboard) {
    const r = row as {
      solutionRovider?: string;
      keyboardType?: string;
      productId?: number;
      vendorId?: number;
      is4G?: boolean;
      adaptation?: number[];
    };
    if (r.solutionRovider !== "beiying" || r.keyboardType !== "99") {
      continue;
    }
    const meta = { is4G: r.is4G, vendorId: r.vendorId };
    if (typeof r.productId === "number") {
      byPid.set(r.productId, meta);
    }
    if (Array.isArray(r.adaptation)) {
      for (const pid of r.adaptation) {
        if (typeof pid === "number") byPid.set(pid, meta);
      }
    }
  }
  return [...byPid.entries()].map(
    ([pid, meta]) =>
      new ByHidFilter(
        pid,
        meta.vendorId ?? BEIYING_DEFAULT_VENDOR_ID,
        undefined,
        undefined,
        meta.is4G === true
      )
  );
}

/** ES87 2.4G 接收器 usagePage（0x258a），暂与实机写死一致 */
export const BEIYING_ES87_WIRELESS_USAGE_PAGE = 9610;

/** ES87 2.4G 接收器 VID（与 supportedDevices 无线行 0x258a 一致） */
export const BEIYING_ES87_WIRELESS_VENDOR_ID = 0x258a;

export function isBeiyingEs87WirelessConfiguration(configuration: {
  keyboardType?: string;
  vendorId?: number;
  is4G?: boolean;
}): boolean {
  if (String(configuration?.keyboardType ?? "") !== "es87") return false;
  return (
    configuration?.is4G === true ||
    configuration?.vendorId === BEIYING_ES87_WIRELESS_VENDOR_ID
  );
}

export function buildBeiyingKeyboardHidFilters(configuration: {
  keyboardType?: string;
  productId?: number;
  vendorId?: number;
  is4G?: boolean;
  beiyingWirelessConnectType?: string;
}): ByHidFilter[] {
  if (shouldUseEs98SingleProductHidFilter(configuration)) {
    const vendorId = configuration.vendorId ?? BEIYING_DEFAULT_VENDOR_ID;
    return [
      new ByHidFilter(
        configuration.productId!,
        vendorId,
        undefined,
        undefined,
        isBeiyingDeviceDetectorWirelessFilter(configuration)
      ),
    ];
  }
  if (configuration?.keyboardType === "99") {
    return buildBeiyingEs98HidFilters();
  }
  if (typeof configuration?.productId !== "number") {
    return [];
  }
  if (isBeiyingEs87WirelessConfiguration(configuration)) {
    return [
      new ByHidFilter(
        configuration.productId,
        BEIYING_ES87_WIRELESS_USAGE_PAGE
      ),
    ];
  }
  if (configuration?.keyboardType === "es87") {
    return [new ByHidFilter(configuration.productId)];
  }
  /**
   * X98 等 web-hid 键盘：ByHidFilter 默认 VID=0x372e。
   * 2.4G 须标 isWireless；SDK 仅对 PID 0x106c 走 Wireless8K 读 UUID，其余无线走 Wireless。
   */
  if (typeof configuration.vendorId === "number") {
    return [
      new ByHidFilter(
        configuration.productId,
        configuration.vendorId,
        undefined,
        undefined,
        isBeiyingDeviceDetectorWirelessFilter(configuration)
      ),
    ];
  }
  return [new ByHidFilter(configuration.productId)];
}

/**
 * web-hid 2.4G Keyboard.connectType（对齐 SDK 注释）：
 * - 贝盈原生 8K 接收器 PID 0x106c：ConnectType.Wireless8K（2026 年前历史协议）
 * - ES98 1K 2.4G 接收器 PID 0x10d5：配置表 beiyingWirelessConnectType 可覆盖
 * - ES87 / 中颖 0x02d7 等：ConnectType.Wireless（2026+ 三模无线，8K/1K 共用）
 *
 * 中颖若误用 Wireless8K，灯光页 getEffect / 布局初始化等会全部命令超时。
 */
export function resolveBeiyingWebHidWirelessConnectType(configuration: {
  keyboardType?: string;
  vendorId?: number;
  productId?: number;
  is4G?: boolean;
  beiyingWirelessConnectType?: string;
}): ConnectType {
  if (isBeiyingEs87WirelessConfiguration(configuration)) {
    return ConnectType.Wireless;
  }
  const override = String(
    configuration?.beiyingWirelessConnectType ?? ""
  ).toLowerCase();
  if (override === "wireless8k") return ConnectType.Wireless8K;
  if (override === "wireless") return ConnectType.Wireless;
  if (Number(configuration?.productId) === BEIYING_LEGACY_24G_PRODUCT_ID) {
    return ConnectType.Wireless8K;
  }
  return ConnectType.Wireless;
}

/**
 * 贝盈键盘 HID productName 与配置表是否匹配。
 * es87 接收器上报名称常与 displayname 不一致，VID/PID 已唯一时放宽。
 */
export function beiyingKeyboardHidProductNameMatches(
  adaptationItem: any,
  hidItem: any
): boolean {
  const n = (v: unknown) =>
    typeof v === "string" ? v.trim().toLowerCase() : "";
  const hidPn = n(hidItem?.productName);
  const cfgPn = n(adaptationItem?.productName);

  if (
    adaptationItem?.keyboardType === "m80" ||
    adaptationItem?.keyboardType === "m71" ||
    adaptationItem?.keyboardType === "t87" ||
    adaptationItem?.keyboardType === "x75v2" ||
    adaptationItem?.keyboardType === "m75"
  ) {
    return !hidPn || (Boolean(cfgPn) && cfgPn === hidPn);
  }
  if (adaptationItem?.keyboardType === "99") {
    return !hidPn || cfgPn === hidPn;
  }
  if (adaptationItem?.keyboardType === "es87") {
    return true;
  }
  /** X98 8K 2.4G：贝盈接收器常报「8K Wireless Receiver」；中颖报配置表 productName */
  if (adaptationItem?.keyboardType === "98" && adaptationItem?.is4G === true) {
    return (
      adaptationItem?.productName == hidItem?.productName ||
      hidPn === "8k wireless receiver" ||
      (Boolean(cfgPn) && cfgPn === hidPn)
    );
  }
  return adaptationItem?.productName == hidItem?.productName;
}

/** DeviceDetector 返回的 Map 键可能是 number / bigint / string，与配置表 uuid 直接 get 会失败 */
export function getBeiyingUuid2Device(
  uuid2Device: Map<unknown, unknown> | null | undefined,
  uuid: unknown
): unknown {
  if (uuid2Device == null || uuid == null) return undefined;
  if (typeof uuid2Device.get !== "function") return undefined;
  const direct = uuid2Device.get(uuid as never);
  if (direct != null) return direct;
  const want = String(uuid);
  const wantNum = Number(uuid);
  for (const key of uuid2Device.keys()) {
    if (key == uuid || String(key) === want) return uuid2Device.get(key);
    if (
      typeof wantNum === "number" &&
      !Number.isNaN(wantNum) &&
      Number(key) === wantNum
    ) {
      return uuid2Device.get(key);
    }
  }
  return undefined;
}

/** connectList 中的 device：常为 HIDDevice[]，也可能是单个 HIDDevice；空数组 / 过渡态会导致 [0] 为 undefined */
export function firstHidFromConnectEntry(deviceField: unknown): any {
  if (deviceField == null) return undefined;
  if (Array.isArray(deviceField)) {
    return deviceField.length > 0 ? deviceField[0] : undefined;
  }
  return deviceField;
}

/** 匠盟：卡片 stableId 与 mouseService 实例 id 对齐（精确匹配或 cid/mid/type 一致） */
export function findJiangmengConnectedStableId(
  stableId: string | null | undefined,
  connectedIds: string[]
): string | null {
  if (!stableId || !Array.isArray(connectedIds) || connectedIds.length === 0) {
    return null;
  }
  for (const id of connectedIds) {
    if (jiangmengMouseStableIdsMatch(stableId, id)) return id;
  }
  return null;
}

export const JIANGMENG_MOUSE_VENDOR_REPORT_ID = 0x08;

/** 匠盟 Vendor 接口（reportId 0x08） */
export function jiangmengHidHasVendor08Interface(d: any): boolean {
  return !!d?.collections?.some((c: any) => {
    const { inputReports, outputReports } = c;
    const hasOutput08 =
      Array.isArray(outputReports) &&
      outputReports.some(
        (r: any) => r.reportId === JIANGMENG_MOUSE_VENDOR_REPORT_ID
      );
    const hasInput08 =
      Array.isArray(inputReports) &&
      inputReports.some(
        (r: any) => r.reportId === JIANGMENG_MOUSE_VENDOR_REPORT_ID
      );
    return hasOutput08 || hasInput08;
  });
}

/** 匠盟：同一物理接收器（仅 serial 非空且一致，或同一 HIDDevice 引用） */
export function jiangmengHidSamePhysicalUnit(a: any, b: any): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  if (
    Number(a.vendorId) !== Number(b.vendorId) ||
    Number(a.productId) !== Number(b.productId)
  ) {
    return false;
  }
  const sa = String(a.serialNumber ?? "").trim();
  const sb = String(b.serialNumber ?? "").trim();
  return sa !== "" && sb !== "" && sa === sb;
}

/** 卡片是否已绑定该 HID（禁止用空 serial 的 VID+PID 粗匹配） */
export function jiangmengCardCoversTargetHid(item: any, target: any): boolean {
  if (item?.configuration?.solutionRovider !== "jiangmeng" || !target) {
    return false;
  }
  const raw = item.device;
  if (raw === target) return true;
  if (Array.isArray(raw) && raw.some((d) => d === target)) return true;
  const d = firstHidFromConnectEntry(raw);
  if (d === target) return true;
  return jiangmengHidSamePhysicalUnit(d, target);
}

/** 匠盟产品线身份（cid/mid/type，如 S9 Pro v2 mid=11 vs S9 Ultra v2 mid=10） */
export function jiangmengProductLineIdentity(item: any): string | null {
  const di = item?.deviceInfo;
  const cid = di?.cid;
  const mid = di?.mid;
  const typ = di?.type;
  if (
    typeof cid === "number" &&
    typeof mid === "number" &&
    typeof typ === "number"
  ) {
    return `${cid}-${mid}-${typ}`;
  }
  if (item?.stableId != null && String(item.stableId).trim() !== "") {
    const parts = String(item.stableId).split("-");
    if (parts.length >= 7) {
      return `${parts[parts.length - 3]}-${parts[parts.length - 2]}-${parts[parts.length - 1]}`;
    }
  }
  return null;
}

/** 同一 HID 分组内各物理匠盟鼠标（vendor 0x08 代表接口） */
export function listJiangmengVendor08PhysicalReps(devices: any[]): any[] {
  const list = Array.isArray(devices)
    ? devices
    : devices != null
      ? [devices]
      : [];
  const valid = list.filter(jiangmengHidHasVendor08Interface);
  if (!valid.length) return [];

  const anySerial = valid.some(
    (d) => String(d.serialNumber ?? "").trim() !== ""
  );
  if (!anySerial) {
    // 同 VID/PID 多型号（如 S9 Pro v2 / S9 Ultra v2 均为 0xf517「EWEADN 8K」且无 serial）：
    // 逐个 vendor 0x08 接口尝试连接，mouseService 会对同 dongle 多接口复用实例
    return valid;
  }

  const seen = new Set<string>();
  const reps: any[] = [];
  for (const d of valid) {
    const key = hidPhysicalKey(d);
    if (seen.has(key)) continue;
    seen.add(key);
    reps.push(d);
  }
  return reps;
}

export function jiangmengSupportedVidPidKeys(): Set<string> {
  return new Set(
    (SUPPORTED_DEVICES.mouse as any[])
      .filter(
        (r: any) =>
          r?.solutionRovider === "jiangmeng" &&
          r.vendorId != null &&
          r.productId != null
      )
      .map((r: any) => `${r.vendorId}-${r.productId}`)
  );
}

const normHuafendaId = (s: unknown) =>
  typeof s === "string" ? s.trim() : String(s ?? "").trim();

/**
 * 按华奋达 SDK getDeviceId() 返回值解析 supportedDevices 行（避免同 PID 落到第一款 X87）。
 */
export function resolveHuafendaSupportedDeviceByIdentifier(
  deviceId: string,
  workModeHint?: string | number,
  product?: string | number
): Record<string, unknown> | undefined {
  const id = normHuafendaId(deviceId);
  if (!id) return undefined;

  const kb = SUPPORTED_DEVICES.keyboard as Record<string, unknown>[];
  const huafendaRows = kb.filter((r) => r.solutionRovider === "huafenda");

  const disambiguateByProduct = (
    rows: Record<string, unknown>[]
  ): Record<string, unknown> | undefined => {
    if (rows.length === 1) return rows[0];
    if (rows.length > 1 && product) {
      const byProduct = rows.find((r) => r.product == product);
      if (byProduct) return byProduct;
    }
    return undefined;
  };

  // 包含匹配：全华奋达 2.4G（含 X87 V2 / X87 Neon / X75 / M98 等）SDK 全量 id ⊇ 配置裸 id
  const containHits = huafendaRows
    .filter((r) => huafendaConfigIdentifierMatchesDeviceId(r.identifier, id))
    .sort(
      (a, b) =>
        normHuafendaId(b.identifier).length -
        normHuafendaId(a.identifier).length
    );
  const containRes = disambiguateByProduct(containHits);
  if (containRes) return containRes;
  if (containHits.length > 1 && workModeHint !== undefined) {
    const wm = String(workModeHint);
    const byWm = containHits.filter((r) => String(r.workMode ?? "") === wm);
    const wmContainRes = disambiguateByProduct(byWm);
    if (wmContainRes) return wmContainRes;
    if (byWm.length === 1) return byWm[0];
  }
  if (containHits.length === 1) return containHits[0];

  const idLower = id.toLowerCase();
  const tail = idLower.split(":").pop() ?? "";
  if (tail) {
    const suffixHits = huafendaRows.filter((r) => {
      const ri = normHuafendaId(r.identifier).toLowerCase();
      return ri.endsWith(`:${tail}`) || ri.includes(tail);
    });
    const suffixRes = disambiguateByProduct(suffixHits);
    if (suffixRes) return suffixRes;
    if (suffixHits.length > 1 && workModeHint !== undefined) {
      const wm = String(workModeHint);
      const byWm = suffixHits.filter((r) => String(r.workMode ?? "") === wm);
      const wmSuffixRes = disambiguateByProduct(byWm);
      if (wmSuffixRes) return wmSuffixRes;
    }
    if (/dk108|34817|dongle:1:26/.test(idLower)) {
      const dk108 = suffixHits.find((r) => r.keyboardType === "DK108");
      if (dk108) return dk108 as Record<string, unknown> | undefined;
    }
    if (/m98|muse-m98/.test(tail)) {
      if (product != null) {
        const byProduct = suffixHits.find((r) => r.product == product);
        if (byProduct) return byProduct as Record<string, unknown> | undefined;
      }
      return suffixHits.find((r) => r.keyboardType === "M98") as
        | Record<string, unknown>
        | undefined;
    }
  }

  return undefined;
}

/** 华奋达 2.4G 接收器 WebHID productId（含 X87 V2 的 0xfef9） */
export const HFD_24G_DONGLE_PRODUCT_ID = 0xfefe;

export function isHuafenda24gDongleHid(hid: any): boolean {
  if (isNk20ReceiverHid(hid)) return false;
  return isHuafendaDongleWebHid(hid);
}

export { isNk20ReceiverHid };

/** @deprecated 请用 HFD_24G_DONGLE_PRODUCT_IDS / isHuafendaDongleWebHid */
export { HFD_24G_DONGLE_PRODUCT_IDS };

function huafendaDongleInstanceKeyFromHid(hid: any): string | null {
  if (!isHuafenda24gDongleHid(hid)) return null;
  const ser = String(hid.serialNumber ?? "").trim();
  return `${hid.vendorId}:${hid.productId}:${ser || "__noserial__"}`;
}

/** 列表中是否已有卡片绑定该 2.4G 接收器（同 VID/PID 即视为同一 dongle） */
export function connectListHasHuafendaCardForDongleHid(
  list: any[],
  dongleHid: any
): boolean {
  if (!dongleHid || !Array.isArray(list) || list.length === 0) return false;
  return list.some((item) => {
    if (item?.configuration?.solutionRovider !== "huafenda") return false;
    const d = firstHidFromConnectEntry(item.device);
    if (!d) return false;
    return (
      Number(d.vendorId) === Number(dongleHid.vendorId) &&
      Number(d.productId) === Number(dongleHid.productId)
    );
  });
}

/** deviceInfo.identifier 与 configuration.identifier 一致时更可信（SDK 实机型号） */
function huafendaCardIdentifierConsistencyScore(item: any): number {
  const cfgId = String(item?.configuration?.identifier ?? "").trim();
  const diId = String(item?.deviceInfo?.identifier ?? "").trim();
  if (cfgId && diId && cfgId === diId) return 2;
  if (diId) return 1;
  if (cfgId) return 0;
  return -1;
}

/** 同一 dongle 只保留一张卡片，优先保留 SDK identifier 与配置一致的项 */
function dedupeHuafendaSharedDongleCards(list: any[]): any[] {
  if (!Array.isArray(list) || list.length <= 1) return list ?? [];
  const bestByDongle = new Map<
    string,
    { item: any; index: number; score: number }
  >();
  list.forEach((item, index) => {
    const d = firstHidFromConnectEntry(item?.device);
    const dk = d ? huafendaDongleInstanceKeyFromHid(d) : null;
    if (
      !dk ||
      item?.configuration?.solutionRovider !== "huafenda" ||
      !isHuafenda24gDongleHid(d)
    ) {
      return;
    }
    const score = huafendaCardIdentifierConsistencyScore(item);
    const cur = bestByDongle.get(dk);
    if (
      !cur ||
      score > cur.score ||
      (score === cur.score && index < cur.index)
    ) {
      bestByDongle.set(dk, { item, index, score });
    }
  });
  if (bestByDongle.size === 0) return list;
  const drop = new Set<any>();
  list.forEach((item) => {
    const d = firstHidFromConnectEntry(item?.device);
    const dk = d ? huafendaDongleInstanceKeyFromHid(d) : null;
    if (!dk || !bestByDongle.has(dk)) return;
    if (item !== bestByDongle.get(dk)!.item) drop.add(item);
  });
  return drop.size ? list.filter((item) => !drop.has(item)) : list;
}

/**
 * 华奋达：有线与 2.4G 接收器 VID/PID、SDK identifier 均不同，但属同一「型号卡片」。
 * 用 vendorId + keyboardType + displayname 作为产品线去重键。
 */
export function huafendaCardDedupeKey(configuration: any): string | null {
  if (configuration?.solutionRovider !== "huafenda") return null;
  if (configuration.vendorId == null || !configuration.keyboardType)
    return null;
  const norm = (s: unknown) =>
    typeof s === "string" ? s.trim().toLowerCase() : "";
  const dn = String(
    configuration.displayname ?? configuration.productName ?? ""
  );
  return `${configuration.vendorId}-${String(configuration.keyboardType)}-${norm(dn)}`;
}

/** 华奋达 2.4G 接收器在 WebHID 中 productName 常为 2.4G Dongle，与配置表型号名不同 */
function isHuafenda24GDongleHidName(hid: any): boolean {
  if (isNk20ReceiverHid(hid)) return false;
  const pn = normProductName(hid?.productName);
  return pn.includes("dongle") || pn.includes("2.4g");
}

function isHuafenda24GConfiguration(configuration: any): boolean {
  return (
    configuration?.solutionRovider === "huafenda" &&
    String(configuration?.workMode ?? "") === "2"
  );
}

/** 华奋达：配置表 productName 与 WebHID productName 可能不一致（如 X75 Pro Screen / X87 V2 + 2.4G Dongle） */
function huafendaAuthorizedHidMatchesConfiguration(
  hid: any,
  configuration: any,
  storedHid?: any
): boolean {
  if (
    Number(hid.vendorId) !== Number(configuration?.vendorId) ||
    Number(hid.productId) !== Number(configuration?.productId)
  ) {
    return false;
  }

  const cfgPn = normProductName(configuration?.productName);
  const livePn = normProductName(hid?.productName);
  if (cfgPn && livePn && cfgPn === livePn) return true;

  if (
    isHuafenda24GConfiguration(configuration) &&
    isHuafenda24GDongleHidName(hid)
  ) {
    return true;
  }

  const cardKey = huafendaCardDedupeKey(configuration);
  if (cardKey && huafendaDedupeKeysForHidGroup([hid]).has(cardKey)) {
    return true;
  }

  const disp = normProductName(configuration?.displayname ?? "");
  if (disp && livePn && (disp.includes(livePn) || livePn.includes(disp))) {
    return true;
  }

  if (storedHid) return hidSamePhysicalUsb(storedHid, hid);
  return false;
}

/** 星闪：同 VID/PID + 展示名只保留一张卡片（避免多 HID 分组重复 autoconnect） */
export function xingshanCardDedupeKey(configuration: any): string | null {
  if (configuration?.solutionRovider !== "xingshan") return null;
  if (configuration.vendorId == null || configuration.productId == null) {
    return null;
  }
  const norm = (s: unknown) =>
    typeof s === "string" ? s.trim().toLowerCase() : "";
  const vidPidKey = `${configuration.vendorId}-${configuration.productId}`;
  const label = KEYBOARD_VID_PID_NEED_PRODUCT_SPLIT.has(vidPidKey)
    ? String(configuration.productName ?? configuration.displayname ?? "")
    : String(configuration.displayname ?? configuration.productName ?? "");
  return `${configuration.vendorId}-${configuration.productId}-${norm(label)}`;
}

/** 容园：同 VID/PID + productName 只保留一张卡片 */
export function rongyuanCardDedupeKey(configuration: any): string | null {
  if (!isRongyuanMechanicalKeyboard(configuration)) return null;
  if (configuration.vendorId == null || configuration.productId == null) {
    return null;
  }
  const norm = (s: unknown) =>
    typeof s === "string" ? s.trim().toLowerCase() : "";
  return `${configuration.vendorId}-${configuration.productId}-${norm(configuration.productName)}-${norm(configuration.displayname)}`;
}

/** 设备卡片唯一键（合并持久化与内存列表、子应用回首页恢复多设备） */
export function connectListEntryIdentityKey(item: any): string | null {
  const hf = huafendaCardDedupeKey(item?.configuration);
  if (hf) return `hf:${hf}`;
  const zx = zhwxCardDedupeKey(item?.configuration);
  if (zx) return `zx:${zx}`;
  const sx = sdcxCardDedupeKey(item?.configuration);
  if (sx) return `sx:${sx}`;
  const xs = xingshanCardDedupeKey(item?.configuration);
  if (xs) return `xs:${xs}`;
  const ry = rongyuanCardDedupeKey(item?.configuration);
  if (ry) return `ry:${ry}`;
  const bk = beiyingKeyboardLogicalKey(item?.configuration);
  if (bk) return `bk:${bk}`;
  const bm = beiyingMouseCardIdentityKey(item);
  if (bm) return `bm:${bm}`;
  if (item?.configuration?.solutionRovider === "jiangmeng") {
    const jm = jiangmengProductLineIdentity(item);
    if (jm) return `jm-line:${jm}`;
  }
  if (item?.stableId != null && String(item.stableId).trim() !== "") {
    return `sid:${item.stableId}`;
  }
  const g = connectListItemPreferWiredGroupKey(item);
  if (g) return `g:${g}`;
  return null;
}

/** 合并多份 connectList，按设备身份去重（后者不覆盖前者同键项） */
export function mergeConnectListByDeviceIdentity(...lists: any[][]): any[] {
  const out: any[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      const key = connectListEntryIdentityKey(item);
      const dedupeKey =
        key ??
        `anon:${item?.configuration?.vendorId}-${item?.configuration?.productId}-${out.length}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push(item);
    }
  }
  return out;
}

/** 星闪：同一 SDK device.id 只保留一张卡片（历史错误 VID 配置行会与实机行并存） */
export function dedupeXingshanConnectListBySdkDeviceId(list: any[]): any[] {
  if (!Array.isArray(list) || list.length <= 1) return list ?? [];
  const seen = new Set<unknown>();
  return list.filter((item) => {
    if (item?.configuration?.solutionRovider !== "xingshan") return true;
    const id = item?.device?.id;
    if (id == null) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** 将星闪卡片 configuration 对齐到 getDevices() 实机（纠正 0x1ca6 / 0x1a86 混用） */
export function normalizeXingshanConnectListEntries(
  list: any[],
  xsDevices: any[] | null | undefined
): any[] {
  if (!Array.isArray(list) || list.length === 0) return list ?? [];
  const byId = new Map<unknown, any>();
  for (const d of xsDevices ?? []) {
    if (d?.id != null) byId.set(d.id, d);
  }
  return list.map((entry) => {
    if (entry?.configuration?.solutionRovider !== "xingshan") return entry;
    const id = entry?.device?.id;
    const live = id != null ? byId.get(id) : entry?.device;
    if (!live) return entry;
    const resolved = resolveXingshanConfigurationRowForDevice(live);
    if (!resolved) {
      const cfg = stampOtherMenuOnlyIfGenericXingshanHid(
        entry.configuration,
        live?.productName
      );
      return {
        ...entry,
        device: live,
        ...(cfg !== entry.configuration ? { configuration: cfg } : {}),
      };
    }
    if (xingshanHidMatchesConfiguration(entry.configuration, live)) {
      const cfg = stampOtherMenuOnlyIfGenericXingshanHid(
        entry.configuration,
        live?.productName
      );
      if (entry.device === live && cfg === entry.configuration) return entry;
      return { ...entry, device: live, configuration: cfg };
    }
    return {
      ...entry,
      device: live,
      configuration: stampOtherMenuOnlyIfGenericXingshanHid(
        { ...resolved },
        live?.productName
      ),
    };
  });
}

/** 首页设备条：同产品线去重（华奋达 / 星闪） */
export function dedupeConnectListByProductLine(list: any[]): any[] {
  if (!Array.isArray(list) || list.length <= 1) return list ?? [];
  const seen = new Set<string>();
  const byLine = list.filter((item) => {
    const hf = huafendaCardDedupeKey(item?.configuration);
    const xs = xingshanCardDedupeKey(item?.configuration);
    const ry = rongyuanCardDedupeKey(item?.configuration);
    const key = hf ? `hf:${hf}` : xs ? `xs:${xs}` : ry ? `ry:${ry}` : null;
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return dedupeHuafendaSharedDongleCards(
    dedupeXingshanConnectListBySdkDeviceId(byLine)
  );
}

const normProductName = (s: unknown) =>
  typeof s === "string" ? s.trim().toLowerCase() : "";

/** 富芮坤卡片：须有 type（连接模式），否则首页 rescan 不应视为已建卡完成 */
export function isFreqConnectEntryInfoComplete(item: any): boolean {
  if (item?.configuration?.solutionRovider !== "freq") return true;
  const t = item?.deviceInfo?.type;
  return typeof t === "number" && Number.isFinite(t);
}

/** 展立卡片：须有 isWired（VID/PID 连接模式），否则首页 rescan 不应视为已建卡完成 */
export function isZhanliConnectEntryInfoComplete(item: any): boolean {
  if (item?.configuration?.solutionRovider !== "zhanli") return true;
  return typeof item?.deviceInfo?.isWired === "boolean";
}

/** 已授权 HID 分组是否已被 connectList 中的卡片覆盖（避免回首页重复 autoconnect 弹授权框） */
export function isHidGroupCoveredByConnectList(
  group: unknown,
  listSnapshot: any[]
): boolean {
  const groupArr = Array.isArray(group) ? group : group ? [group] : [];
  if (groupArr.some(isNk20ReceiverHid)) return true;

  const first = groupArr[0];
  if (!first || !Array.isArray(listSnapshot) || listSnapshot.length === 0) {
    return false;
  }

  /** 原生 8K 多接收器：按物理 dongle 判断是否已有卡，不能只看 uuid 是否存在于任意卡片 */
  if (groupArr.some(isBeiyingNative8KDongleHid)) {
    return listSnapshot.some(
      (it) =>
        it?.configuration?.solutionRovider === "beiying" &&
        it?.configuration?.keyboardType &&
        beiyingConnectListItemCoversHidGroup(it, groupArr)
    );
  }

  const groupNameSet = new Set(
    groupArr.map((h: any) => normProductName(h.productName))
  );

  const dongleHid = groupArr.find(isHuafenda24gDongleHid);
  if (
    dongleHid &&
    connectListHasHuafendaCardForDongleHid(listSnapshot, dongleHid)
  ) {
    return true;
  }

  const incomingHuafendaKeys = huafendaDedupeKeysForHidGroup(groupArr);
  if (
    incomingHuafendaKeys.size > 0 &&
    listSnapshot.some((item: any) => {
      const k = huafendaCardDedupeKey(item.configuration);
      return k != null && incomingHuafendaKeys.has(k);
    })
  ) {
    return true;
  }

  const jmVidPidKeys = jiangmengSupportedVidPidKeys();
  const jmGroupKey =
    first?.vendorId != null && first?.productId != null
      ? `${first.vendorId}-${first.productId}`
      : "";
  if (jmGroupKey && jmVidPidKeys.has(jmGroupKey)) {
    const reps = listJiangmengVendor08PhysicalReps(groupArr);
    if (!reps.length) return false;
    return reps.every((rep) =>
      listSnapshot.some((item) => jiangmengCardCoversTargetHid(item, rep))
    );
  }

  const hidSamePhysicalUsb = (a: any, b: any) => {
    if (!a || !b) return false;
    if (
      Number(a.vendorId) !== Number(b.vendorId) ||
      Number(a.productId) !== Number(b.productId)
    ) {
      return false;
    }
    const s = String(a.serialNumber ?? "").trim();
    const l = String(b.serialNumber ?? "").trim();
    if (s === "" || l === "") return true;
    return s === l;
  };

  return listSnapshot.some((item: any) => {
    const d = firstHidFromConnectEntry(item.device);
    const cfg = item.configuration;
    if (!d) return false;
    if (
      Number(d.vendorId) !== Number(first.vendorId) ||
      Number(d.productId) !== Number(first.productId)
    ) {
      return false;
    }

    if (cfg?.solutionRovider === "huafenda") {
      const cardKey = huafendaCardDedupeKey(cfg);
      if (cardKey) {
        const incomingKeys = huafendaDedupeKeysForHidGroup(groupArr);
        if (incomingKeys.has(cardKey)) return true;
      }
      if (
        isHuafenda24GConfiguration(cfg) &&
        groupArr.some((h) => isHuafenda24GDongleHidName(h))
      ) {
        return true;
      }
      const cfgPn = normProductName(cfg.productName);
      if (cfgPn && groupNameSet.has(cfgPn)) return true;
      const disp = normProductName(cfg?.displayname ?? "");
      if (disp && groupNameSet.size > 0) {
        for (const pn of groupNameSet) {
          if (disp.includes(pn) || pn.includes(disp)) return true;
        }
      }
      return false;
    }

    if (cfg?.solutionRovider === "beiying" && !cfg.keyboardType) {
      const cardHid = firstHidFromConnectEntry(item.device);
      if (!cardHid) return false;
      return groupArr.some((h: any) =>
        beiyingMouseHidMatchesCardHid(cardHid, h)
      );
    }

    if (cfg?.solutionRovider === "beiying" && cfg.keyboardType) {
      const logic = beiyingKeyboardLogicalKey(cfg);
      if (logic) {
        return listSnapshot.some(
          (it) => beiyingKeyboardLogicalKey(it.configuration) === logic
        );
      }
      return hidSamePhysicalUsb(d, first);
    }

    if (cfg?.solutionRovider === "freq") {
      // 不可仅靠 VID/PID/productName：X23 SE 与 X23SE 同名但 deviceId/displayname 不同
      if (
        normProductName(d.productName) !== normProductName(first.productName) ||
        !isFreqConnectEntryInfoComplete(item)
      ) {
        return false;
      }
      const cardSer = String(d.serialNumber ?? "").trim();
      const groupSer = String(first.serialNumber ?? "").trim();
      if (cardSer && groupSer) return cardSer === groupSer;
      // 无 serial 时 hidPhysicalKey 会撞车，只能认同一 HIDDevice 引用
      return groupArr.some((h: any) => h === d);
    }

    if (cfg?.solutionRovider === "zhanli") {
      if (
        normProductName(d.productName) !== normProductName(first.productName) ||
        !isZhanliConnectEntryInfoComplete(item)
      ) {
        return false;
      }
      return hidSamePhysicalUsb(d, first);
    }

    if (cfg?.solutionRovider === "xingshan") {
      return listSnapshot.some(
        (it) =>
          it?.configuration?.solutionRovider === "xingshan" &&
          xingshanHidMatchesConfiguration(it.configuration, first)
      );
    }

    if (isRongyuanMechanicalKeyboard(cfg)) {
      const cardKey = rongyuanCardDedupeKey(cfg);
      if (cardKey) {
        return listSnapshot.some(
          (it) => rongyuanCardDedupeKey(it.configuration) === cardKey
        );
      }
      return groupArr.some((h: any) => rongyuanHidMatchesConfiguration(cfg, h));
    }

    if (hasDeviceExternalUrl(cfg)) {
      return groupArr.some((h: any) =>
        externalUrlHidProductNameMatches(h.productName, cfg)
      );
    }

    return hidSamePhysicalUsb(d, first);
  });
}

export type XingshanDisconnectHint = {
  sdkId?: unknown;
  /** SDK usbChange 或 WebHID disconnect 事件中的设备对象 */
  device?: any;
};

/** 星闪卡片是否与本次拔插/断开事件为同一台设备（id 或 VID/PID+名称） */
export function xingshanDisconnectMatchesEntry(
  entry: any,
  hint: XingshanDisconnectHint
): boolean {
  if (entry?.configuration?.solutionRovider !== "xingshan") return false;
  const cardId = entry?.device?.id;
  if (hint.sdkId != null && cardId != null && cardId === hint.sdkId) {
    return true;
  }
  const evt = hint.device;
  const cfg = entry?.configuration;
  if (!evt || !cfg) return false;
  if (evt.vendorId !== cfg.vendorId || evt.productId !== cfg.productId) {
    return false;
  }
  const cfgPn = normProductName(cfg?.productName ?? cfg?.displayname);
  const evtPn = normProductName(evt?.productName);
  if (cfgPn && evtPn && cfgPn !== evtPn) return false;
  if (cardId != null && evt.id != null && cardId === evt.id) return true;
  const lineKey = xingshanCardDedupeKey(cfg);
  if (!lineKey) return true;
  const evtKey = `${evt.vendorId}-${evt.productId}-${normProductName(
    evt?.productName ?? ""
  )}`;
  if (lineKey === evtKey) return true;
  return !cfgPn || !evtPn;
}

/** 拔掉星闪设备后从 connectList 移除对应卡片 */
export function removeXingshanConnectEntries(
  list: any[],
  hint: XingshanDisconnectHint
): any[] {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.filter((entry) => !xingshanDisconnectMatchesEntry(entry, hint));
}

/** 卡片是否仍对应 getDevices() 中某台已授权 HID（与 autoconnect 覆盖规则一致） */
export function isConnectEntryStillAuthorized(
  entry: any,
  authorizedHidFlat: any[],
  xingshanLiveDevices: any[] | ReadonlyMap<unknown, any>,
  forceRemoveXingshanIds?: ReadonlySet<unknown>
): boolean {
  const cfg = entry?.configuration;
  if (cfg?.solutionRovider === "xingshan") {
    const id = entry?.device?.id;
    if (id != null && forceRemoveXingshanIds?.has(id)) return false;
    const xingshanById =
      xingshanLiveDevices instanceof Map
        ? xingshanLiveDevices
        : new Map(
            (Array.isArray(xingshanLiveDevices) ? xingshanLiveDevices : [])
              .filter((d) => d?.id != null)
              .map((d) => [d.id, d])
          );
    if (id == null || !xingshanById.has(id)) return false;
    const live = xingshanById.get(id);
    if (!live || !xingshanHidMatchesConfiguration(cfg, live)) return false;
    // 固件升级 Boot 重连：仅校验 SDK 仍持有该 id（勿因 WebHID 短暂为空误删卡）
    if (isKeyboardFirmwareUpgradeInProgress()) return true;
    // 日常：必须仍在浏览器 WebHID 已授权列表中（取消站点授权后不应保留卡片）
    if (!Array.isArray(authorizedHidFlat) || authorizedHidFlat.length === 0) {
      return false;
    }
    return authorizedHidFlat.some((hid) =>
      xingshanHidMatchesConfiguration(cfg, hid)
    );
  }
  if (!Array.isArray(authorizedHidFlat) || authorizedHidFlat.length === 0) {
    return false;
  }
  const storedHid = firstHidFromConnectEntry(entry?.device);
  const vid = Number(cfg?.vendorId ?? storedHid?.vendorId);
  const pid = Number(cfg?.productId ?? storedHid?.productId);
  if (!Number.isFinite(vid) || !Number.isFinite(pid)) {
    return false;
  }
  if (
    authorizedHidFlat.some((hid) =>
      isHidGroupCoveredByConnectList([hid], [entry])
    )
  ) {
    return true;
  }
  // 持久化 JSON 常丢失 device 字段，仅凭 configuration 与已授权 HID 对齐
  if (!storedHid) {
    return authorizedHidFlat.some((hid) => {
      if (Number(hid.vendorId) !== vid || Number(hid.productId) !== pid) {
        return false;
      }
      if (cfg?.solutionRovider === "beiying" && cfg?.keyboardType) {
        return beiyingHidMatchesEquipmentConfiguration(hid, cfg);
      }
      if (cfg?.solutionRovider === "huafenda") {
        return huafendaAuthorizedHidMatchesConfiguration(hid, cfg);
      }
      if (cfg?.solutionRovider === "freq" || cfg?.solutionRovider === "freqC") {
        const cfgPn = normProductName(cfg?.productName ?? cfg?.displayname);
        const livePn = normProductName(hid?.productName);
        if (cfgPn && livePn && cfgPn !== livePn) return false;
        // 无 storedHid 时无法绑物理机，仅按 productName 保留（与旧行为兼容）
        return true;
      }
      if (cfg?.solutionRovider === "xingshan") {
        return xingshanHidMatchesConfiguration(cfg, hid);
      }
      if (isRongyuanMechanicalKeyboard(cfg)) {
        return (
          rongyuanHidMatchesConfiguration(cfg, hid) &&
          (hid.collections ?? []).some((c: any) =>
            rongyuanHidCollectionMatches(cfg, c)
          )
        );
      }
      if (hasDeviceExternalUrl(cfg)) {
        return externalUrlHidProductNameMatches(hid?.productName, cfg);
      }
      if (cfg?.solutionRovider === "beiying" && !cfg?.keyboardType) {
        return (
          isBeiyingMouseHidDevice(hid) &&
          Number(hid.vendorId) === vid &&
          Number(hid.productId) === pid
        );
      }
      return true;
    });
  }
  return authorizedHidFlat.some((hid) => {
    if (Number(hid.vendorId) !== vid || Number(hid.productId) !== pid) {
      return false;
    }
    if (cfg?.solutionRovider === "beiying" && cfg?.keyboardType) {
      return beiyingHidMatchesEquipmentConfiguration(hid, cfg);
    }
    if (cfg?.solutionRovider === "huafenda") {
      return huafendaAuthorizedHidMatchesConfiguration(hid, cfg, storedHid);
    }
    if (cfg?.solutionRovider === "freq" || cfg?.solutionRovider === "freqC") {
      const cfgPn = normProductName(cfg?.productName ?? cfg?.displayname);
      const livePn = normProductName(hid?.productName);
      if (cfgPn && livePn && cfgPn !== livePn) return false;
      // 空 serial 时 physicalKey 会撞车，只能认同一 HIDDevice 或非空 serial
      if (storedHid === hid) return true;
      return hidSamePhysicalUsb(storedHid, hid);
    }
    if (cfg?.solutionRovider === "jiangmeng") {
      if (
        entry.stableId != null &&
        String(entry.stableId).trim() !== "" &&
        hid === storedHid
      ) {
        return true;
      }
      return jiangmengHidSamePhysicalUnit(storedHid, hid);
    }
    if (cfg?.solutionRovider === "xingshan") {
      return xingshanHidMatchesConfiguration(cfg, hid);
    }
    if (isRongyuanMechanicalKeyboard(cfg)) {
      return (
        rongyuanHidMatchesConfiguration(cfg, hid) &&
        (hid.collections ?? []).some((c: any) =>
          rongyuanHidCollectionMatches(cfg, c)
        )
      );
    }
    if (hasDeviceExternalUrl(cfg)) {
      return externalUrlHidProductNameMatches(hid?.productName, cfg);
    }
    if (cfg?.solutionRovider === "beiying" && !cfg?.keyboardType) {
      return beiyingMouseHidMatchesCardHid(storedHid, hid);
    }
    return hidSamePhysicalUsb(storedHid, hid);
  });
}

function flattenAuthorizedHidGroups(authorizedHidGroups: unknown[]): any[] {
  const flat: any[] = [];
  const groups = Array.isArray(authorizedHidGroups) ? authorizedHidGroups : [];
  for (const group of groups) {
    const arr = Array.isArray(group) ? group : group ? [group] : [];
    flat.push(...arr);
  }
  return flat;
}

/**
 * 从 connectList 中移除浏览器已取消 WebHID 授权、或星闪 SDK 中已不存在的设备卡片。
 */
export function filterConnectListToAuthorized(
  list: any[],
  authorizedHidGroups: unknown[],
  xingshanLiveDevices: any[],
  forceRemoveXingshanIds?: ReadonlySet<unknown>
): any[] {
  if (!Array.isArray(list) || list.length === 0) return [];
  const flat = flattenAuthorizedHidGroups(authorizedHidGroups);
  return list.filter((entry) =>
    isConnectEntryStillAuthorized(
      entry,
      flat,
      xingshanLiveDevices,
      forceRemoveXingshanIds
    )
  );
}

/** HID 组内各 interface 在配置表中可能命中的华奋达产品线去重键（用于 sync 判断是否已有卡片） */
export function huafendaDedupeKeysForHidGroup(group: any[]): Set<string> {
  const keys = new Set<string>();
  const arr = Array.isArray(group) ? group : [group];
  const norm = (s: unknown) =>
    typeof s === "string" ? s.trim().toLowerCase() : "";
  const kb = SUPPORTED_DEVICES.keyboard as any[];
  for (const hid of arr) {
    if (hid?.vendorId == null || hid?.productId == null) continue;
    const hidPn = norm(hid.productName);
    const matched: any[] = [];
    for (const row of kb) {
      if (row.solutionRovider !== "huafenda") continue;
      if (row.vendorId !== hid.vendorId || row.productId !== hid.productId)
        continue;
      const rowPn = norm(row.productName);
      if (hidPn && rowPn && hidPn !== rowPn) {
        // 2.4G 接收器 HID 常为「2.4G Dongle」，配置表 productName 为型号名（如 X87 V2 / X87 Neon）
        if (
          !(
            isHuafenda24GDongleHidName(hid) &&
            String(row.workMode ?? "") === "2"
          )
        ) {
          continue;
        }
      }
      matched.push(row);
    }
    /** 同 PID 多款 2.4G 接收器（productName 均为 2.4G Dongle）无法从 HID 区分，勿误判「已覆盖」而跳过建卡 */
    if (matched.length !== 1) continue;
    const key = huafendaCardDedupeKey(matched[0]);
    if (key) keys.add(key);
  }
  return keys;
}

/** 贝盈鼠标 HID：用于热插拔前置锁判断，避免与贝盈键盘共用一个「beiying」方案商名时互相挡住 */
export function isBeiyingMouseHidDevice(device: any): boolean {
  if (device?.vendorId == null || device?.productId == null) return false;
  const mice = SUPPORTED_DEVICES.mouse as any[];
  return mice.some(
    (m) =>
      m?.solutionRovider === "beiying" &&
      typeof m.vendorId === "number" &&
      typeof m.productId === "number" &&
      m.vendorId === device.vendorId &&
      m.productId === device.productId
  );
}

/** 同一贝盈键盘可能有多 interface（PID 不同），卡片应以 uuid 合并；无 uuid 时退回 vid+pid+keyboardType */
export function beiyingKeyboardLogicalKey(configuration: any): string | null {
  if (configuration?.solutionRovider !== "beiying") return null;
  if (!configuration?.keyboardType) return null;
  const u = configuration.uuid?.[0];
  if (u !== undefined && u !== null) {
    return `beiying-kbd-uuid-${String(u)}`;
  }
  return `beiying-kbd-${configuration.vendorId}-${configuration.productId}-${configuration.keyboardType}`;
}

/** 固件 UUID 与配置表 uuid[0] 对齐（兼容 number / bigint / 十六进制字符串） */
export function beiyingFirmwareUuidEquals(
  firmwareUuid: unknown,
  configUuid: unknown
): boolean {
  if (firmwareUuid == null || configUuid == null) return false;
  try {
    const a = BigInt(String(firmwareUuid as string | number | bigint));
    const b = BigInt(String(configUuid as string | number | bigint));
    return a === b;
  } catch {
    return String(firmwareUuid) === String(configUuid);
  }
}

/**
 * M71 / T87 / X75V2 有线：同 VID+PID+productName，仅靠 HID 无法区分型号；返回配置表中所有可能行（用于 UUID 探测）。
 */
/** 贝盈原生 8K 接收器：ES98 / X98 8K 共用 VID/PID */
export const BEIYING_NATIVE_8K_DONGLE_VENDOR_ID = 0x372e;
export const BEIYING_NATIVE_8K_DONGLE_PRODUCT_ID = 0x106c;

export function isBeiyingNative8KDongleHid(hid: any): boolean {
  return (
    hid != null &&
    Number(hid.vendorId) === BEIYING_NATIVE_8K_DONGLE_VENDOR_ID &&
    Number(hid.productId) === BEIYING_NATIVE_8K_DONGLE_PRODUCT_ID
  );
}

/** 贝盈键盘：同一物理接收器（serial 或 WebHID id） */
export function beiyingKeyboardHidSamePhysicalUnit(a: any, b: any): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  if (
    Number(a.vendorId) !== Number(b.vendorId) ||
    Number(a.productId) !== Number(b.productId)
  ) {
    return false;
  }
  const sa = String(a.serialNumber ?? "").trim();
  const sb = String(b.serialNumber ?? "").trim();
  if (sa !== "" && sb !== "") return sa === sb;
  if (a.id != null && b.id != null) return String(a.id) === String(b.id);
  return false;
}

/** connectList 卡片是否覆盖该 HID 分组（同物理接收器） */
export function beiyingConnectListItemCoversHidGroup(
  item: any,
  groupArr: any[]
): boolean {
  const cardHid = firstHidFromConnectEntry(item?.device);
  if (!cardHid || !Array.isArray(groupArr) || groupArr.length === 0) {
    return false;
  }
  return groupArr.some((h) => beiyingKeyboardHidSamePhysicalUnit(h, cardHid));
}

/** SDK / DeviceDetector 解析出的设备是否属于当前 HID 分组（避免扫到其它 dongle 的 uuid） */
export function beiyingSdkDeviceMatchesHidGroup(
  sdkDev: any,
  hidDevices: any[]
): boolean {
  if (!sdkDev || !Array.isArray(hidDevices) || hidDevices.length === 0) {
    return false;
  }
  if (hidDevices.some((h) => beiyingKeyboardHidSamePhysicalUnit(sdkDev, h))) {
    return true;
  }
  const norm = (v: unknown) =>
    typeof v === "string" ? v.trim().toLowerCase() : "";
  const sdkPn = norm(sdkDev.productName);
  if (!sdkPn) return false;
  const es98Like = (s: string) =>
    s.includes("es98") || s === "8k wireless receiver";
  const x98Like = (s: string) => s === "x98" || s.startsWith("x98 ");
  return hidDevices.some((h) => {
    const pn = norm(h.productName);
    if (!pn) return false;
    if (pn === sdkPn) return true;
    if (es98Like(pn) && es98Like(sdkPn)) return true;
    if (x98Like(pn) && x98Like(sdkPn)) return true;
    return false;
  });
}

/** 原生 8K 接收器：优先选主 interface 做 DeviceDetector anchor（跳过副接口名） */
export function pickPreferredNative8KDongleAnchorHid(hidDevices: any[]): any {
  if (!Array.isArray(hidDevices) || hidDevices.length === 0) return undefined;
  const norm = (v: unknown) =>
    typeof v === "string" ? v.trim().toLowerCase() : "";
  const primary = hidDevices.find((h) => {
    const pn = norm(h?.productName);
    return pn !== "" && pn !== "8k wireless receiver";
  });
  return primary ?? hidDevices[0];
}

/**
 * ES98 / X98 8K 共用贝盈原生 8K 接收器：不依赖 HID productName，始终列出全部 uuid 候选供探测。
 * 首次 WebHID 授权后名称可能尚未稳定为「8K Wireless Receiver」，仅靠名称匹配会漏触发探测。
 */
export function listBeiyingEs98X988KNativeDongleProbeCandidates(
  hid: any
): any[] {
  if (!isBeiyingNative8KDongleHid(hid)) return [];
  const kb = SUPPORTED_DEVICES.keyboard as any[];
  const byUuid = new Map<string, any>();
  for (const row of kb) {
    if (
      row?.solutionRovider === "beiying" &&
      (row.keyboardType === "98" || row.keyboardType === "99") &&
      row.is4G === true &&
      row.vendorId === BEIYING_NATIVE_8K_DONGLE_VENDOR_ID &&
      row.productId === BEIYING_NATIVE_8K_DONGLE_PRODUCT_ID &&
      Array.isArray(row.uuid) &&
      row.uuid.length > 0 &&
      !isBeiyingDevice8815X98Keyboard(row) &&
      row.sdkType !== SDK_KEYBOARD_MOUSE_TYPE.keyboard.beiyingMachine
    ) {
      const key = String(row.uuid[0]);
      if (!byUuid.has(key)) byUuid.set(key, row);
    }
  }
  const unique = [...byUuid.values()];
  return unique.length > 1 ? unique : [];
}

/** 2.4G web-hid 接收器 UUID 探测候选：原生 8K 走 ES98 专用列表，其余仍按 productName 消歧 */
export function listBeiyingWebHidDongleProbeCandidatesForHid(hid: any): any[] {
  if (isBeiyingNative8KDongleHid(hid)) {
    return listBeiyingEs98X988KNativeDongleProbeCandidates(hid);
  }
  return listBeiyingWebHidDongleAmbiguousConfigsForHid(hid);
}

/**
 * X98 8K / ES98 等同 VID+PID 2.4G 接收器：HID 名称可能相同，需 DeviceDetector + uuid 探测型号。
 */
export function listBeiyingWebHidDongleAmbiguousConfigsForHid(hid: any): any[] {
  if (!hid || hid.vendorId == null || hid.productId == null) return [];
  const kb = SUPPORTED_DEVICES.keyboard as any[];
  const matched = kb.filter(
    (row) =>
      row?.solutionRovider === "beiying" &&
      (row.keyboardType === "98" || row.keyboardType === "99") &&
      row.is4G === true &&
      row.vendorId === hid.vendorId &&
      row.productId === hid.productId &&
      Array.isArray(row.uuid) &&
      row.uuid.length > 0 &&
      !isBeiyingDevice8815X98Keyboard(row) &&
      row.sdkType !== SDK_KEYBOARD_MOUSE_TYPE.keyboard.beiyingMachine &&
      beiyingKeyboardHidProductNameMatches(row, hid)
  );
  const byUuid = new Map<string, any>();
  for (const row of matched) {
    const key = String(row.uuid[0]);
    if (!byUuid.has(key)) byUuid.set(key, row);
  }
  const unique = [...byUuid.values()];
  return unique.length > 1 ? unique : [];
}

export function listBeiying916WiredAmbiguousConfigsForHid(hid: any): any[] {
  if (!hid || hid.vendorId == null || hid.productId == null) return [];
  const kb = SUPPORTED_DEVICES.keyboard as any[];
  const n = (v: unknown) =>
    typeof v === "string" ? v.trim().toLowerCase() : "";
  const hidPn = n(hid.productName);
  return kb.filter(
    (row) =>
      row?.solutionRovider === "beiying" &&
      (row.keyboardType === "m71" ||
        row.keyboardType === "t87" ||
        row.keyboardType === "x75v2" ||
        row.keyboardType === "m75") &&
      row.is4G !== true &&
      typeof row.vendorId === "number" &&
      typeof row.productId === "number" &&
      row.vendorId === hid.vendorId &&
      row.productId === hid.productId &&
      Array.isArray(row.uuid) &&
      row.uuid.length > 0 &&
      (!hidPn || !n(row.productName) || n(row.productName) === hidPn)
  );
}

/**
 * M80 / T87 / X75V2 2.4G：同 VID+PID+productName，仅靠 HID 无法区分型号；返回配置表中所有可能行（用于 UUID 探测）。
 */
export function listBeiying916WirelessAmbiguousConfigsForHid(hid: any): any[] {
  if (!hid || hid.vendorId == null || hid.productId == null) return [];
  const kb = SUPPORTED_DEVICES.keyboard as any[];
  const n = (v: unknown) =>
    typeof v === "string" ? v.trim().toLowerCase() : "";
  const hidPn = n(hid.productName);
  return kb.filter(
    (row) =>
      row?.solutionRovider === "beiying" &&
      (row.keyboardType === "m80" ||
        row.keyboardType === "t87" ||
        row.keyboardType === "x75v2" ||
        row.keyboardType === "m75") &&
      row.is4G === true &&
      typeof row.vendorId === "number" &&
      typeof row.productId === "number" &&
      row.vendorId === hid.vendorId &&
      row.productId === hid.productId &&
      Array.isArray(row.uuid) &&
      row.uuid.length > 0 &&
      (!hidPn || !n(row.productName) || n(row.productName) === hidPn)
  );
}

export function hidSerialPartitionKey(hid: any): string {
  const s = String(hid?.serialNumber ?? "").trim();
  return s !== "" ? s : "__empty_serial__";
}

/** 同 VID/PID 下凭 HID serial 判定是否同一物理 USB 设备（serial 为空时不视为同机） */
export function hidSamePhysicalUsb(a: any, b: any): boolean {
  return (
    !!a &&
    !!b &&
    a.vendorId === b.vendorId &&
    a.productId === b.productId &&
    String(a.serialNumber ?? "").trim() !== "" &&
    String(a.serialNumber ?? "") === String(b.serialNumber ?? "")
  );
}

const normProductNameDisconnect = (s: unknown) =>
  String(s ?? "")
    .trim()
    .toLowerCase();

/** ES98 家族任一 interface（有线 / 2.4G 等 PID）断开时，应移除对应卡片 */
function hidMatchesBeiyingEs98FamilyDisconnect(hid: any, cfg: any): boolean {
  if (
    cfg?.solutionRovider !== "beiying" ||
    cfg?.keyboardType !== "99" ||
    hid?.vendorId == null ||
    hid?.productId == null
  ) {
    return false;
  }
  if (Number(hid.vendorId) !== Number(cfg.vendorId)) return false;
  const kb = SUPPORTED_DEVICES.keyboard as any[];
  return kb.some(
    (row) =>
      row?.solutionRovider === "beiying" &&
      row?.keyboardType === "99" &&
      row?.vendorId === cfg.vendorId &&
      row?.productId === hid.productId
  );
}

/** M71 / T87 / X75V2 / M75 机甲：同 VID/PID 且 HID 常无 serial，不能仅靠 hidSamePhysicalUsb */
export function isBeiyingMechaM71OrT87KeyboardType(
  keyboardType: unknown
): boolean {
  const kt = String(keyboardType ?? "").toLowerCase();
  return kt === "m71" || kt === "t87" || kt === "x75v2" || kt === "m75";
}

export function matchBeiyingMechaM71OrT87HidDisconnect(
  disconnectedDevice: any,
  configuration: any,
  connectEntry?: { device?: any } | null
): boolean {
  if (
    configuration?.solutionRovider !== "beiying" ||
    !isBeiyingMechaM71OrT87KeyboardType(configuration?.keyboardType)
  ) {
    return false;
  }
  if (
    disconnectedDevice?.vendorId !== configuration.vendorId ||
    disconnectedDevice?.productId !== configuration.productId
  ) {
    return false;
  }

  const hidList = Array.isArray(connectEntry?.device)
    ? connectEntry.device
    : connectEntry?.device != null
      ? [connectEntry.device]
      : [];

  if (
    hidList.some((d: any) => d === disconnectedDevice) ||
    connectEntry?.device === disconnectedDevice
  ) {
    return true;
  }

  for (const d of hidList) {
    if (hidSamePhysicalUsb(d, disconnectedDevice)) {
      return true;
    }
  }

  return beiyingHidMatchesEquipmentConfiguration(
    disconnectedDevice,
    configuration
  );
}

/** 内页：当前 session 是否为 M71/T87 物理断开 */
export function isBeiyingMechaM71OrT87ActiveSessionDisconnect(
  disconnectedDevice: any,
  configurationNow: any,
  connectList: any[]
): boolean {
  if (
    !configurationNow ||
    !isBeiyingMechaM71OrT87KeyboardType(configurationNow?.keyboardType) ||
    configurationNow?.solutionRovider !== "beiying"
  ) {
    return false;
  }
  const wantKey = beiyingKeyboardLogicalKey(configurationNow);
  const hit = connectList.find(
    (it: any) => beiyingKeyboardLogicalKey(it?.configuration) === wantKey
  );
  return matchBeiyingMechaM71OrT87HidDisconnect(
    disconnectedDevice,
    configurationNow,
    hit
  );
}

/**
 * 热插拔 disconnect：判定首页卡片是否应随该 HID interface 移除。
 * 物理断开后 getDevices() 仍可能保留已授权项，不能仅靠 reconcile 裁剪。
 */
export function connectEntryShouldRemoveOnHidDisconnect(
  entry: any,
  disconnectedDevice: any
): boolean {
  if (!entry || !disconnectedDevice) return false;

  const hidList = Array.isArray(entry.device)
    ? entry.device
    : entry.device != null
      ? [entry.device]
      : [];

  if (
    hidList.some((d: any) => d === disconnectedDevice) ||
    entry.device === disconnectedDevice
  ) {
    return true;
  }

  if (
    hidList.some(
      (d: any) =>
        d === disconnectedDevice || hidSamePhysicalUsb(d, disconnectedDevice)
    )
  ) {
    return true;
  }

  const deviceTarget = hidList[0];
  if (!deviceTarget) return false;

  const pnCard = normProductNameDisconnect(deviceTarget.productName);
  const pnEvt = normProductNameDisconnect(disconnectedDevice.productName);

  const vidPidMatch =
    deviceTarget.productId === disconnectedDevice.productId &&
    deviceTarget.vendorId === disconnectedDevice.vendorId;
  if (!vidPidMatch) {
    if (
      entry.configuration?.vendorId === disconnectedDevice.vendorId &&
      hidMatchesBeiyingEs98FamilyDisconnect(
        disconnectedDevice,
        entry.configuration
      )
    ) {
      return true;
    }
    return false;
  }

  const cfg = entry.configuration;
  if (hasDeviceExternalUrl(cfg)) {
    return externalUrlHidProductNameMatches(
      disconnectedDevice.productName,
      cfg
    );
  }

  const sol = cfg?.solutionRovider;
  const cfgKt = cfg?.keyboardType;
  const isMechaKb =
    sol === "beiying" &&
    (cfgKt === "m80" ||
      cfgKt === "m71" ||
      cfgKt === "t87" ||
      cfgKt === "x75v2" ||
      cfgKt === "m75");

  if (sol === "freq" || sol === "freqC") {
    return Boolean(pnCard && pnEvt && pnCard === pnEvt);
  }

  if (
    isMechaKb &&
    (cfgKt === "m71" || cfgKt === "t87" || cfgKt === "x75v2" || cfgKt === "m75")
  ) {
    return matchBeiyingMechaM71OrT87HidDisconnect(
      disconnectedDevice,
      cfg,
      entry
    );
  }

  if (isMechaKb && pnCard && pnEvt && pnCard !== pnEvt) {
    return false;
  }

  if (sol === "beiying" && cfgKt) {
    if (cfgKt === "99") {
      return hidMatchesBeiyingEs98FamilyDisconnect(disconnectedDevice, cfg);
    }
    if (!beiyingHidMatchesEquipmentConfiguration(disconnectedDevice, cfg)) {
      return false;
    }
  }

  return true;
}

/**
 * 将卡片上的 equipment 与单个 HID 对齐（与 `useConnectDevice` 内贝盈匹配规则一致），
 * 用于同 VID/PID 多物理键盘时选中正确 HID 分组 / 避免内页连错机。
 */
export function beiyingHidMatchesEquipmentConfiguration(
  hid: any,
  cfg: any
): boolean {
  if (!hid || !cfg || cfg.solutionRovider !== "beiying" || !cfg.keyboardType) {
    return false;
  }
  if (hid.vendorId !== cfg.vendorId || hid.productId !== cfg.productId) {
    return false;
  }
  return beiyingKeyboardHidProductNameMatches(cfg, hid);
}

/** 贝盈连接排错：只摘控制台可读的关键字段 */
export function beiyingMouseHidLogSnapshot(device: any) {
  if (device == null) return null;
  return {
    vendorId: device.vendorId,
    productId: device.productId,
    productName: device.productName,
    opened: device.opened,
  };
}

export function beiyingMouseConfigLogSnapshot(configuration: any) {
  if (configuration == null) return null;
  return {
    vendorId: configuration.vendorId,
    productId: configuration.productId,
    productName: configuration.productName,
    displayname: configuration.displayname,
    hidReportId: configuration.hidReportId,
    is4G: configuration.is4G,
  };
}

/** WebHID 设备简要信息，便于「暂未接入」时控制台排错 */
export function hidDeviceConsoleSnapshot(device: any) {
  if (device == null) return null;
  return {
    vendorId: device.vendorId,
    productId: device.productId,
    productName: device.productName,
  };
}

/** DeviceDetector uuid 探测失败时，是否可凭 VID/PID 唯一性回退 vendor HID（不含原生 8K 0x106c 多 uuid 场景） */
export function canBypassBeiyingDeviceDetectorUuidProbe(
  configuration: any,
  anchorHid: any
): boolean {
  if (!configuration || !anchorHid) return false;
  if (isBeiyingNative8KDongleHid(anchorHid)) return false;
  if (listBeiyingWebHidDongleProbeCandidatesForHid(anchorHid).length > 1) {
    return false;
  }
  if (
    typeof configuration.vendorId === "number" &&
    Number(anchorHid.vendorId) !== Number(configuration.vendorId)
  ) {
    return false;
  }
  if (
    typeof configuration.productId === "number" &&
    Number(anchorHid.productId) !== Number(configuration.productId)
  ) {
    return false;
  }
  return true;
}

/** 从已授权 HID 中选取命中 ByHidFilter 的 vendor interface（优先 opened） */
export async function pickBeiyingFilterMatchedVendorHid(
  hidFilters: ByHidFilter[],
  preferredHid?: any
): Promise<any | null> {
  if (!hidFilters.length) return null;
  if (
    preferredHid &&
    DeviceDetector.isSupportedDevice(preferredHid, hidFilters)
  ) {
    return preferredHid;
  }
  let authorizedHids: any[] = [];
  try {
    authorizedHids = await (navigator as any).hid.getDevices();
  } catch {
    return null;
  }
  const matched = authorizedHids.filter((hid) =>
    DeviceDetector.isSupportedDevice(hid, hidFilters)
  );
  if (!matched.length) return null;
  return matched.find((hid) => hid.opened) ?? matched[0];
}

/**
 * 贝盈 web-hid 键盘：DeviceDetector 解析 HID；uuid 探测为空且 VID/PID 唯一时回退 vendor HID。
 * 中颖 X98 8K 2.4G（0x258a/0x02d7）等设备过滤器可命中但 SDK 建链前 uuid 读失败时走 bypass。
 */
export async function resolveBeiyingWebHidKeyboardHidDevice(args: {
  configuration: any;
  resolvedUuid: unknown;
  anchorHid?: any;
}): Promise<{
  beiyingDevices: unknown;
  deviceDetector: DeviceDetector | null;
}> {
  const empty = { beiyingDevices: null, deviceDetector: null };
  const { configuration, resolvedUuid, anchorHid } = args;
  if (resolvedUuid == null) return empty;

  const hidFilters = buildBeiyingKeyboardHidFilters(configuration);
  if (!hidFilters.length) return empty;

  const deviceDetector = new DeviceDetector(hidFilters, [Number(resolvedUuid)]);
  try {
    const uuid2Device = (await deviceDetector.getDevices()) as Map<
      unknown,
      unknown
    >;
    const beiyingDevices = getBeiyingUuid2Device(uuid2Device, resolvedUuid);
    if (beiyingDevices != null) {
      return { beiyingDevices, deviceDetector };
    }
  } catch {
    /* uuid 探测失败，尝试 bypass */
  }
  try {
    deviceDetector.destroy();
  } catch {
    /* ignore */
  }

  if (canBypassBeiyingDeviceDetectorUuidProbe(configuration, anchorHid)) {
    const bypassHid = await pickBeiyingFilterMatchedVendorHid(
      hidFilters,
      anchorHid
    );
    if (bypassHid != null) {
      return { beiyingDevices: bypassHid, deviceDetector: null };
    }
  }

  return empty;
}

const normLower = (s: unknown) =>
  typeof s === "string" ? s.trim().toLowerCase() : "";

/**
 * 贝盈鼠标卡片唯一身份（禁止仅用 VID+PID）：运行时 uuid 优先，否则 HID serial + 配置行 uuid 首项 + 展示名 + HID 名称。
 */
/** 贝盈鼠标 VID/PID 集合（同接收器型号多实例场景） */
export function beiyingMouseSupportedVidPidKeys(): Set<string> {
  const keys = new Set<string>();
  const mice = SUPPORTED_DEVICES.mouse as Array<{
    solutionRovider?: string;
    vendorId?: number;
    productId?: number;
  }>;
  for (const m of mice) {
    if (
      m?.solutionRovider === "beiying" &&
      typeof m.vendorId === "number" &&
      typeof m.productId === "number"
    ) {
      keys.add(`${m.vendorId}-${m.productId}`);
    }
  }
  return keys;
}

/**
 * 贝盈鼠标：同 VID/PID 多接收器时，仅 WebHID 同一对象或非空且一致的 serial 视为同一物理机。
 * 空 serial 不能宽匹配，否则刷新 rescan 会误判「已覆盖」而只建一张卡。
 */
export function beiyingMouseHidMatchesCardHid(
  cardHid: any,
  candidate: any
): boolean {
  if (!cardHid || !candidate) return false;
  if (cardHid === candidate) return true;
  if (
    Number(cardHid.vendorId) !== Number(candidate.vendorId) ||
    Number(cardHid.productId) !== Number(candidate.productId)
  ) {
    return false;
  }
  const s = String(cardHid.serialNumber ?? "").trim();
  const c = String(candidate.serialNumber ?? "").trim();
  return s !== "" && c !== "" && s === c;
}

export function beiyingMouseCardIdentityKey(item: any): string | null {
  const cfg = item?.configuration;
  if (!cfg || cfg.solutionRovider !== "beiying" || cfg.keyboardType) {
    return null;
  }
  const di = item?.deviceInfo;
  const ru = di?.uuid;
  if (ru != null && String(ru).trim() !== "") {
    return `uuid:${String(ru)}:${cfg.vendorId}:${cfg.productId}`;
  }
  const d = firstHidFromConnectEntry(item?.device);
  if (
    cfg.vendorId == null ||
    cfg.productId == null ||
    d?.vendorId == null ||
    d?.productId == null
  ) {
    return null;
  }
  const serial =
    d.serialNumber != null && String(d.serialNumber).trim() !== ""
      ? String(d.serialNumber)
      : "";
  const hidPn = normLower(d.productName);
  const dn = normLower(cfg.displayname ?? cfg.productName ?? "");
  const cfgUuid0 =
    Array.isArray(cfg.uuid) && cfg.uuid[0] != null ? String(cfg.uuid[0]) : "";
  return `hid:${cfg.vendorId}:${cfg.productId}:${cfgUuid0}:${serial}:${hidPn}:${dn}`;
}

/** 与 {@link beiyingMouseCardIdentityKey} 同一规则，用于连接入参侧比对 */
export function beiyingMouseIncomingIdentity(args: {
  deviceInfo: any;
  resolvedConfiguration: any;
  devices: any;
}): string | null {
  const { deviceInfo, resolvedConfiguration, devices } = args;
  const cfg = resolvedConfiguration;
  if (!cfg || cfg.solutionRovider !== "beiying" || cfg.keyboardType) {
    return null;
  }
  const ru = deviceInfo?.uuid;
  if (ru != null && String(ru).trim() !== "") {
    return `uuid:${String(ru)}:${cfg.vendorId}:${cfg.productId}`;
  }
  const d = firstHidFromConnectEntry(devices);
  if (
    cfg.vendorId == null ||
    cfg.productId == null ||
    d?.vendorId == null ||
    d?.productId == null
  ) {
    return null;
  }
  const serial =
    d.serialNumber != null && String(d.serialNumber).trim() !== ""
      ? String(d.serialNumber)
      : "";
  const hidPn = normLower(d.productName);
  const dn = normLower(cfg.displayname ?? cfg.productName ?? "");
  const cfgUuid0 =
    Array.isArray(cfg.uuid) && cfg.uuid[0] != null ? String(cfg.uuid[0]) : "";
  return `hid:${cfg.vendorId}:${cfg.productId}:${cfgUuid0}:${serial}:${hidPn}:${dn}`;
}

/** 连接方式：USB 有线 / 2.4G 无线（用于性能埋点与卡片归并） */
export function resolveConnectListConnectionType(item: any): "USB" | "2.4G" {
  return isConnectListItemWirelessTransmission(item) ? "2.4G" : "USB";
}

/**
 * 首页后台探测：不对 2.4G 接收器建 dongle SDK / 读 HID（固件角标、休眠、电量轮询）。
 * 进子应用后的建链与子应用内读版本不受影响。
 */
export function shouldSkipHome24gReceiverBackgroundProbe(item: any): boolean {
  return isConnectListItemWirelessTransmission(item);
}

/** 是否为 2.4G / 无线接收器侧（与有线并存时用于卡片隐藏） */
export function isConnectListItemWirelessTransmission(item: any): boolean {
  const cfg = item?.configuration ?? {};
  if (cfg.is4G === true) return true;
  const wmCfg = cfg.workMode;
  if (wmCfg === 2 || wmCfg === "2") return true;
  const di = item?.deviceInfo ?? {};
  const wm = di.workMode;
  if (wm === 2 || wm === "2") return true;
  const pwrWm = di.power?.workMode;
  if (pwrWm === 2 || pwrWm === "2") return true;
  const pn = normLower(cfg.productName);
  if (pn.includes("2.4g") || pn.includes("dongle")) return true;
  return false;
}

/**
 * 同一物理设备（有线 + 2.4G 两条 connectList）在首页卡片上的归并键：同键内优先只展示有线。
 */
export function connectListItemPreferWiredGroupKey(item: any): string | null {
  const cfg = item?.configuration;
  if (!cfg?.solutionRovider) {
    const d = firstHidFromConnectEntry(item?.device);
    if (d?.vendorId != null && d?.productId != null) {
      const ser =
        d.serialNumber != null && String(d.serialNumber).trim() !== ""
          ? String(d.serialNumber)
          : "";
      return `raw:${d.vendorId}-${d.productId}-${ser}-${normLower(d.productName)}`;
    }
    return null;
  }

  const sol = cfg.solutionRovider;

  if (sol === "huafenda") {
    const k = huafendaCardDedupeKey(cfg);
    return k ? `hf:${k}` : null;
  }

  if (sol === "beiying" && cfg.keyboardType) {
    const logic = beiyingKeyboardLogicalKey(cfg);
    if (logic) return `by:${logic}`;
    if (cfg.vendorId != null && cfg.keyboardType) {
      const dn = normLower(cfg.displayname ?? cfg.productName ?? "");
      return `by-line:${cfg.vendorId}-${String(cfg.keyboardType)}-${dn}`;
    }
    return null;
  }

  if (sol === "beiying" && !cfg.keyboardType) {
    const idKey = beiyingMouseCardIdentityKey(item);
    return idKey ? `bm:${idKey}` : null;
  }

  if (sol === "jiangmeng") {
    if (item.stableId != null && String(item.stableId).trim() !== "") {
      return `jm:${item.stableId}`;
    }
    const d = firstHidFromConnectEntry(item.device);
    if (!d) return null;
    const cid = item.deviceInfo?.cid;
    const mid = item.deviceInfo?.mid;
    const typ = item.deviceInfo?.type;
    if (
      typeof cid === "number" &&
      typeof mid === "number" &&
      typeof typ === "number"
    ) {
      return `jm:id:${d.vendorId}-${d.productId}-${cid}-${mid}-${typ}`;
    }
    const ser =
      d.serialNumber != null && String(d.serialNumber).trim() !== ""
        ? String(d.serialNumber)
        : "";
    return `jm-hid:${d.vendorId}-${d.productId}-${ser}-${normLower(d.productName)}`;
  }

  if (sol === "freq") {
    // 型号维度：同 deviceId 只一张卡（不是同型号两台物理机各一张）
    const did = normLower(cfg.deviceId ?? "");
    if (did) return `fq:did:${did}`;
    if (item.stableId != null && String(item.stableId).trim() !== "") {
      return `fq:${item.stableId}`;
    }
    const d = firstHidFromConnectEntry(item.device);
    if (cfg.vendorId != null && cfg.productId != null) {
      const line = normLower(cfg.displayname ?? cfg.productName ?? "");
      const ser =
        d?.serialNumber != null && String(d.serialNumber).trim() !== ""
          ? String(d.serialNumber)
          : "";
      const hidPn = normLower(d?.productName);
      return `fq-line:${cfg.vendorId}-${cfg.productId}-${line}-${ser}-${hidPn}`;
    }
    return null;
  }

  if (sol === "zhanli") {
    if (item.stableId != null && String(item.stableId).trim() !== "") {
      return `zl:${item.stableId}`;
    }
    const d = firstHidFromConnectEntry(item.device);
    if (cfg.vendorId != null && cfg.productId != null) {
      const dn = normLower(cfg.displayname ?? cfg.productName ?? "");
      const ser =
        d?.serialNumber != null && String(d.serialNumber).trim() !== ""
          ? String(d.serialNumber)
          : "";
      return `zl-hid:${cfg.vendorId}-${cfg.productId}-${dn}-${ser}`;
    }
    return null;
  }

  if (hasDeviceExternalUrl(cfg)) {
    const d = firstHidFromConnectEntry(item?.device);
    if (cfg.vendorId != null && cfg.productId != null) {
      const pn = normLower(cfg.productName ?? "");
      const dn = normLower(cfg.displayname ?? "");
      const hidPn = normLower(d?.productName);
      const ser =
        d?.serialNumber != null && String(d.serialNumber).trim() !== ""
          ? String(d.serialNumber)
          : "";
      return `ext-url:${cfg.vendorId}-${cfg.productId}-${pn}-${dn}-${hidPn}-${ser}`;
    }
    return null;
  }

  if (sol === "xingshan") {
    const id = item?.device?.id;
    if (id != null) return `xs:${id}`;
    if (cfg.vendorId != null && cfg.productId != null) {
      const pn = normLower(cfg.productName ?? cfg.displayname ?? "");
      return `xs-cfg:${cfg.vendorId}-${cfg.productId}-${pn}`;
    }
    return null;
  }

  if (cfg.vendorId != null && cfg.productId != null) {
    const d = firstHidFromConnectEntry(item?.device);
    const dn = normLower(cfg.displayname ?? cfg.productName ?? "");
    const ser =
      d?.serialNumber != null && String(d.serialNumber).trim() !== ""
        ? String(d.serialNumber)
        : "";
    return `gen:${sol}:${cfg.vendorId}-${cfg.productId}-${dn}-${ser}`;
  }

  return null;
}

/** 首页设备条：同归并键下若存在有线条目，则隐藏 2.4G/无线侧卡片（有线拔掉后仅余无线则立刻显示） */
export function filterConnectListPreferWired(list: any[]): any[] {
  if (!Array.isArray(list) || list.length <= 1) return list;
  const hasWiredByKey = new Set<string>();
  for (const item of list) {
    const key = connectListItemPreferWiredGroupKey(item);
    if (!key) continue;
    if (!isConnectListItemWirelessTransmission(item)) {
      hasWiredByKey.add(key);
    }
  }
  return list.filter((item) => {
    const key = connectListItemPreferWiredGroupKey(item);
    if (!key) return true;
    if (hasWiredByKey.has(key) && isConnectListItemWirelessTransmission(item)) {
      return false;
    }
    return true;
  });
}
